import React from "react";
import { DYNAMIC_EVENT_DESIGNS } from "./dynamicEventDesigns";

/**
 * Thumbnail picker for the page design of a dynamic event, with an optional
 * A/B testing mode where several designs are put in rotation.
 *
 * Props:
 *   designId, onDesignChange(id)
 *   abTestEnabled, onAbTestEnabledChange(bool)
 *   abTestDesignIds, onAbTestDesignIdsChange(string[])
 *   onPreviewDesign(id)  - optional: "Preview" link per card
 */
export default function DynamicEventDesignPicker({
  designId,
  onDesignChange,
  abTestEnabled,
  onAbTestEnabledChange,
  abTestDesignIds = [],
  onAbTestDesignIdsChange,
  onPreviewDesign,
}) {
  const isSelected = (id) =>
    abTestEnabled ? abTestDesignIds.includes(id) : designId === id;

  const handleCardClick = (id) => {
    if (!abTestEnabled) {
      onDesignChange(id);
      return;
    }
    const next = abTestDesignIds.includes(id)
      ? abTestDesignIds.filter((d) => d !== id)
      : [...abTestDesignIds, id];
    onAbTestDesignIdsChange(next);
  };

  const rotationTooSmall = abTestEnabled && abTestDesignIds.length < 2;

  return (
    <div className="design-picker">
      <div className="design-picker-head">
        <div>
          <h4 className="design-picker-title">Page design</h4>
          <div className="example-text">
            {abTestEnabled
              ? "Tick every design you want in rotation. Each visitor is shown one at random (sticky for their session) and the design is recorded with their RSVP."
              : "Choose how the public event page looks. You can switch at any time; the content stays the same."}
          </div>
        </div>
        <label className="design-picker-ab form-check form-switch" htmlFor="abTestEnabled">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="abTestEnabled"
            checked={!!abTestEnabled}
            onChange={(e) => onAbTestEnabledChange(e.target.checked)}
          />
          <span className="form-check-label">Enable A/B testing</span>
        </label>
      </div>

      <div
        className={`design-picker-grid ${abTestEnabled ? "is-multi" : ""}`}
        role={abTestEnabled ? "group" : "radiogroup"}
        aria-label="Page design"
      >
        {DYNAMIC_EVENT_DESIGNS.map((design) => {
          const selected = isSelected(design.id);
          return (
            <div
              key={design.id}
              className={`design-card ${selected ? "is-selected" : ""}`}
              role={abTestEnabled ? "checkbox" : "radio"}
              aria-checked={selected}
              tabIndex={0}
              onClick={() => handleCardClick(design.id)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  handleCardClick(design.id);
                }
              }}
            >
              <div className="design-card-thumb">
                <img src={design.thumbnail} alt={`${design.name} design preview`} />
                <span className="design-card-mark" aria-hidden="true">
                  {selected ? (
                    <i className="fa-solid fa-check" />
                  ) : abTestEnabled ? (
                    ""
                  ) : (
                    ""
                  )}
                </span>
              </div>
              <div className="design-card-body">
                <div className="design-card-name">
                  {design.name}
                  {!abTestEnabled && selected ? (
                    <span className="design-card-badge">Selected</span>
                  ) : null}
                  {abTestEnabled && selected ? (
                    <span className="design-card-badge">In rotation</span>
                  ) : null}
                </div>
                <div className="design-card-desc">{design.description}</div>
                {onPreviewDesign ? (
                  <button
                    type="button"
                    className="design-card-preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewDesign(design.id);
                    }}
                  >
                    <i className="fa-solid fa-eye" /> Preview
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {rotationTooSmall ? (
        <div className="design-picker-warning">
          <i className="fa-solid fa-triangle-exclamation" /> Pick at least two designs for
          the A/B test, or turn A/B testing off.
        </div>
      ) : null}
      {abTestEnabled && !rotationTooSmall ? (
        <div className="example-text mt-2">
          Results (page views, RSVPs and conversion per design) appear under <b>View RSVPs</b>{" "}
          for this event once it has traffic.
        </div>
      ) : null}
    </div>
  );
}
